import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Check, Loader2, Pencil, ShieldCheck, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLE_LABEL = { admin: "Admin", case_manager: "Case Manager", hr_scheduler: "HR / Scheduler", evv_specialist: "EVV Specialist", rn: "RN", pcw: "PCW / Caregiver", intake_coordinator: "Intake Coordinator", receptionist: "Receptionist" };

export default function AgencyUsersDialog({ agency, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null);
  const [editing, setEditing] = useState(null); // { id, name }

  const load = async () => {
    try {
      const res = await axios.get(`${API}/platform/agencies/${agency.id}/users`);
      setData(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not load users"); }
  };
  useEffect(() => { if (agency) load(); }, [agency?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const setRole = async (u, role) => {
    setSaving(u.id);
    try {
      await axios.post(`${API}/platform/agencies/${agency.id}/users/${u.id}/role`, { role });
      toast.success(`${u.email} is now ${ROLE_LABEL[role] || role}`);
      await load();
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Role change failed"); }
    finally { setSaving(null); }
  };

  const saveName = async () => {
    if (!editing) return;
    const u = data.users.find((x) => x.id === editing.id);
    setSaving(u.id);
    try {
      await axios.post(`${API}/platform/agencies/${agency.id}/users/${u.id}/role`, { name: editing.name });
      toast.success(`Renamed to ${editing.name.trim()}`);
      setEditing(null);
      await load();
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Rename failed"); }
    finally { setSaving(null); }
  };

  return (
    <Dialog open={!!agency} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="platform-users-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-teal-600" /> Users &amp; roles — {agency?.name}</DialogTitle>
          <DialogDescription>Fix a login's role or display name (click the name to rename). Changes take effect on the person's next sign-in.</DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-3">
            {data.admin_count === 0 && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm" data-testid="platform-users-no-admin">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                This agency has no Admin login — everyone lands on the caregiver portal. Promote the owner below.
              </div>
            )}
            <div className="max-h-[50vh] overflow-y-auto rounded-md border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr><th className="px-3 py-2 text-left">User</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left w-48">Role</th></tr>
                </thead>
                <tbody>
                  {data.users.map((u) => (
                    <tr key={u.id} className="border-t border-slate-100" data-testid={`platform-user-row-${u.id}`}>
                      <td className="px-3 py-2">
                        {editing?.id === u.id ? (
                          <div className="flex items-center gap-1">
                            <Input value={editing.name} onChange={(e) => setEditing({ id: u.id, name: e.target.value })} className="h-7 text-sm w-44" autoFocus
                              onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditing(null); }} data-testid={`platform-user-name-input-${u.id}`} />
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={saveName} disabled={saving === u.id || editing.name.trim().length < 2} data-testid={`platform-user-name-save-${u.id}`}><Check className="w-4 h-4 text-emerald-600" /></Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(null)}><X className="w-4 h-4 text-slate-400" /></Button>
                          </div>
                        ) : (
                          <button type="button" className="group flex items-center gap-1 text-left" onClick={() => setEditing({ id: u.id, name: u.name || "" })} title="Rename" data-testid={`platform-user-name-${u.id}`}>
                            <span className="font-medium text-slate-900">{u.name || "—"}</span>
                            <Pencil className="w-3 h-3 text-slate-300 group-hover:text-slate-500" />
                          </button>
                        )}
                        <p className="text-xs text-slate-500">{u.email}</p>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          <Badge variant="outline" className={u.is_active ? "border-emerald-200 text-emerald-700" : "border-slate-200 text-slate-500"}>{u.is_active ? "Active" : "Inactive"}</Badge>
                          {u.has_employee_record && <Badge variant="outline" className="border-slate-200 text-slate-500">Employee</Badge>}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <Select value={(u.role || "").toLowerCase()} onValueChange={(v) => setRole(u, v)} disabled={saving === u.id}>
                          <SelectTrigger className="h-8" data-testid={`platform-user-role-${u.id}`}><SelectValue placeholder="Role" /></SelectTrigger>
                          <SelectContent>
                            {data.roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r] || r}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                  {data.users.length === 0 && <tr><td colSpan={3} className="px-3 py-8 text-center text-slate-400 italic">No users</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
